// The keys a rail flyout and the views popover share: arrows walk their items, and Tab leaves for the rail.

import type { KeyboardEvent as ReactKeyboardEvent } from "react";

export const FLYOUT_ITEM_SELECTOR = "[data-rail-flyout-item]";

type FlyoutKey = Pick<KeyboardEvent, "key" | "altKey" | "ctrlKey" | "metaKey" | "preventDefault">;

/** ArrowUp / ArrowDown (wrapping), Home and End over the container's items; true and the key claimed when handled. */
export function moveFlyoutFocus(event: FlyoutKey | ReactKeyboardEvent, container: HTMLElement): boolean {
    if (event.altKey || event.ctrlKey || event.metaKey) return false;
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return false;
    const items = [...container.querySelectorAll<HTMLElement>(FLYOUT_ITEM_SELECTOR)];
    if (items.length === 0) return false;

    const active = container.ownerDocument.activeElement;
    const index = items.findIndex((item) => item === active || item.contains(active));
    const last = items.length - 1;
    const target =
        event.key === "Home" ? 0
        : event.key === "End" ? last
        : event.key === "ArrowDown" ? (index === -1 || index === last ? 0 : index + 1)
        : index <= 0 ? last : index - 1;

    event.preventDefault();
    items[target].focus();
    return true;
}

const TABBABLE = [
    "a[href]",
    "button",
    "input:not([type='hidden'])",
    "select",
    "textarea",
    "[tabindex]",
    "[contenteditable]:not([contenteditable='false'])",
].join(",");

function isTabbable(element: HTMLElement, column: HTMLElement): boolean {
    if (element.tabIndex < 0 || (element as HTMLButtonElement).disabled) return false;
    for (let node: HTMLElement | null = element; node && node !== column; node = node.parentElement) {
        if (node.hidden || node.inert) return false;
        const style = getComputedStyle(node);
        if (style.display === "none" || style.visibility === "hidden") return false;
    }
    return true;
}

/**
 * The rail's column (`railColumnClass`). Not `.bg-sidebar` alone, which the rail's head zone paints too.
 */
export const RAIL_COLUMN_SELECTOR = ".bg-sidebar.scrollbar-rail";

/**
 * Focuses the next control after `anchor` in its rail column, which is where Tab would have gone had
 * the flyout not been portalled out of the rail. Null, and nothing focused, where none follows.
 */
export function focusAfter(anchor: HTMLElement): HTMLElement | null {
    const column = anchor.closest<HTMLElement>(RAIL_COLUMN_SELECTOR);
    if (!column) return null;
    for (const candidate of column.querySelectorAll<HTMLElement>(TABBABLE)) {
        const follows = anchor.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING;
        if (!follows || anchor.contains(candidate) || !isTabbable(candidate, column)) continue;
        candidate.focus();
        return candidate;
    }
    return null;
}
